<script  module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { usePage } from "@inertiajs/svelte";
  import { 
    Zap, 
    FileText, 
    Eye, 
    Bot, 
    Search, 
    Clock,
    QrCode,
    TrendingUp,
    Globe,
    Laptop,
    Compass,
    Layers,
    ExternalLink,
    Image as ImageIcon,
  } from "lucide-svelte";
  import { toast } from "svelte-sonner";
  import * as Popover from "$lib/components/ui/popover/index.js";
  import QRCode from '@castlenine/svelte-qrcode';
  import { appStore } from '$lib/stores/appStore';

  interface DashboardProps {
    stats?: {
      lighthouse_score: number;
      page_load_ms: number;
      total_pages: number;
      total_inquiries: number;
      media_usage_mb: number;
      indexed_count: number;
      today_pv: number;
      today_uv: number;
      pv_growth_rate: number;
    };
    recent_activities?: Array<{
      id: string | number;
      action: string;
      target: string;
      time: string;
      type: 'seo' | 'content' | 'system' | 'inquiry';
    }>;
    traffic_trends?: Array<{
      hour: string;
      pv: number;
      uv: number;
    }>;
    rankings?: {
      pages: Array<{ name: string; value: number }>;
      os: Array<{ name: string; value: number }>;
      browsers: Array<{ name: string; value: number }>;
      regions: Array<{ name: string; value: number }>;
    };
    seo_trend?: {
      labels: string[];
      spider_visits: number[];
    };
  }

  let { 
    stats = {
      lighthouse_score: 100,
      page_load_ms: 48,
      total_pages: 0,
      total_inquiries: 0,
      media_usage_mb: 0.0,
      indexed_count: 1,
      today_pv: 0,
      today_uv: 0,
      pv_growth_rate: 0
    }, 
    recent_activities = [], 
    traffic_trends = [],
    rankings = { pages: [], os: [], browsers: [], regions: [] },
    seo_trend = { 
      labels: ["一", "二", "三", "四", "五", "六", "日"], 
      spider_visits: [0, 0, 0, 0, 0, 0, 0] 
    },
  }: DashboardProps = $props();

  const page = usePage();
  
  let currentSite = $derived(page.props?.current_site as any);
  
  // 💡 安全解析多语言变量对象
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.dashboard || {});

  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);
    
    const flash = page.props?.flash as any;
    if (flash?.notice) toast.success(flash.notice);
    if (flash?.alert) toast.error(flash.alert);
  });

  let scoreColor = $derived(
    stats.lighthouse_score >= 90 
      ? 'text-emerald-500 border-emerald-500/20 bg-emerald-500/10' 
      : 'text-amber-500 border-amber-500/20 bg-amber-500/10'
  );

  // 计算趋势图最大值以供高度按比例缩放
  let maxTrafficPv = $derived(Math.max(...traffic_trends.map(t => t.pv), 1));
  let maxSpiderVisits = $derived(Math.max(...seo_trend.spider_visits, 1));
</script>

<svelte:head>
  <title>{t.default_title || '控制台'} | {t.brand_suffix || '分太极光'}</title>
</svelte:head>

<div class="bg-background text-foreground w-full p-1 space-y-6">
  
  <!-- 头部欢迎语与站点上下文 -->
  <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
    <div>
      <h1 class="text-2xl font-bold tracking-tight">{t.welcome_back || '欢迎回来'}</h1>
      <p class="text-sm text-muted-foreground mt-1 flex items-center flex-wrap gap-1">
        {t.current_working_site || '当前工作站点：'}
        <span class="font-semibold text-foreground">
          {currentSite?.name || 'Default Site'}
        </span>
        {#if currentSite?.primary_domain}
          <a 
            href="http://{currentSite.primary_domain}" 
            target="_blank" 
            rel="noopener noreferrer"
            class="inline-flex items-center text-xs text-primary font-medium hover:underline underline-offset-4 bg-primary/5 hover:bg-primary/10 px-2 py-0.5 rounded-md transition-colors ml-1"
            title={t.preview_tip || '点击预览新窗口打开'}
          >
            {currentSite.primary_domain}
            <ExternalLink class="size-3.5"/>
          </a>

          <Popover.Root>
            <Popover.Trigger class="inline-flex items-center justify-center size-5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors cursor-pointer" title={t.show_qrcode || '显示网站二维码'}>
              <QrCode class="size-3.5" />
            </Popover.Trigger>
            
            <Popover.Content class="w-48 p-3 rounded-xl border bg-popover text-popover-foreground shadow-md z-50">
              <div class="flex flex-col items-center gap-2">
                <div class="size-36 bg-white p-1 rounded-lg border border-border flex items-center justify-center">
                  <QRCode data="http://{currentSite.primary_domain}" isResponsive />
                </div>
                <span class="text-[10px] text-muted-foreground text-center truncate w-full">
                  { t.qrcode_title || '手机扫码访问站点' }
                </span>
              </div>
            </Popover.Content>
          </Popover.Root>
        {:else}
          <span class="text-xs text-muted-foreground ml-1">{t.no_domain || '暂未绑定独立域名'}</span>
        {/if}
      </p>
    </div>
  </div>

  <!-- ==================== 🛠️ 核心实时指标卡片区 ==================== -->
  <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    
    <!-- 今日实时流量卡片 -->
    <div class="rounded-xl border bg-card p-5 shadow-sm">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t.card_inquiry || '今日浏览数据'}</p>
        <TrendingUp class="h-4 w-4 text-primary" />
      </div>
      <div class="mt-2 flex items-baseline gap-2">
        <span class="text-3xl font-extrabold tracking-tight text-foreground">{stats.today_pv}</span>
        <span class="text-xs text-muted-foreground">PV / {stats.today_uv} UV</span>
      </div>
      <div class="mt-3 flex items-center gap-1.5">
        {#if stats.pv_growth_rate >= 0}
          <span class="inline-flex items-center rounded-full border border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600">
            +{stats.pv_growth_rate}%
          </span>
        {:else}
          <span class="inline-flex items-center rounded-full border border-rose-500/20 bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600">
            {stats.pv_growth_rate}%
          </span>
        {/if}
        <span class="text-xs text-muted-foreground">{t.card_inquiry_desc || '较昨日同时段环比对比'}</span>
      </div>
    </div>

    <!-- 加载性能卡片 -->
    <div class="rounded-xl border bg-card p-5 shadow-sm">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t.card_load_time || '全栈开屏首载'}</p>
        <Clock class="h-4 w-4 text-sky-500" />
      </div>
      <div class="mt-2 flex items-baseline gap-1">
        <span class="text-3xl font-extrabold tracking-tight text-sky-600">{stats.page_load_ms}</span>
        <span class="text-sm font-semibold text-sky-600">ms</span>
      </div>
      <p class="text-xs text-muted-foreground mt-3">{t.card_load_time_desc || '节点边缘网关真实延迟打点'}</p>
    </div>

    <!-- 性能体检分卡片 -->
    <div class="rounded-xl border bg-card p-5 shadow-sm">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t.card_lighthouse || 'Lighthouse 权重得分'}</p>
        <Zap class="h-4 w-4 text-emerald-500" />
      </div>
      <div class="mt-2 flex items-baseline gap-2">
        <span class="text-3xl font-extrabold tracking-tight {scoreColor.split(' ')[0]}">{stats.lighthouse_score}</span>
        <span class="text-xs text-muted-foreground">/ 100</span>
      </div>
      <div class="mt-3 flex items-center gap-1.5">
        <span class="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold transition-colors {scoreColor}">
          {t.card_lighthouse_badge || '表现极佳'}
        </span>
        <span class="text-xs text-muted-foreground">{t.card_lighthouse_desc || '已由极光优化完成极致渲染'}</span>
      </div>
    </div>

    <!-- 搜索引擎收录卡片 -->
    <div class="rounded-xl border bg-card p-5 shadow-sm">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t.card_seo_title || '收录与留资'}</p>
        <Search class="h-4 w-4 text-amber-500" />
      </div>
      <div class="mt-2 flex items-baseline gap-1">
        <span class="text-3xl font-extrabold tracking-tight text-amber-600">{stats.indexed_count}</span>
        <span class="text-xs text-muted-foreground">/ {stats.total_pages} {t.stat_total_pages_label || '页'}</span>
      </div>
      <p class="text-xs text-muted-foreground mt-3">
        {t.card_inquiry_sub || '累计客户询盘数'}: <strong class="text-foreground">{stats.total_inquiries}</strong>
      </p>
    </div>
  </div>

  <!-- AI 搜索感知 Banner 面板 -->
  <div class="rounded-xl border bg-card p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm bg-gradient-to-r from-primary/5 to-transparent">
    <div class="flex items-center gap-3">
      <div class="p-2 bg-primary/10 rounded-lg text-primary shrink-0">
        <Bot class="size-5" />
      </div>
      <div>
        <h3 class="text-sm font-semibold">{t.ai_banner_title || 'AI 搜索语义引擎就绪'}</h3>
        <p class="text-xs text-muted-foreground mt-0.5">
          {t.ai_banner_desc?.replace('%{ai_models}', t.ai_models_text || '国内外 AI 引擎智能搜索') || '内容已自动同步到国内外 AI 引擎智能搜索语义图谱'}
        </p>
      </div>
    </div>
    <div class="flex items-center gap-4 text-xs text-muted-foreground shrink-0">
      <div class="flex items-center gap-1">
        <FileText class="size-3.5" /> {t.stat_total_pages || '全站网页数'} <strong>{stats.total_pages}</strong>
      </div>
      <div class="flex items-center gap-1">
        <ImageIcon class="size-3.5" /> {t.stat_media_usage || '云媒体占用'} <strong>{stats.media_usage_mb} MB</strong>
      </div>
    </div>
  </div>

  <!-- ==================== 🛠️ 趋势走势图表看板区 ==================== -->
  <div class="grid gap-6 md:grid-cols-2">
    <!-- 今日 24 小时流量时序趋势图 -->
    <div class="rounded-xl border bg-card p-5 shadow-sm flex flex-col">
      <div class="mb-4">
        <h2 class="text-sm font-semibold tracking-tight">{t.traffic_trend_title || '今日 24 小时流量脉搏'}</h2>
        <p class="text-xs text-muted-foreground mt-0.5">{t.traffic_trend_subtitle || '基于 SiteHourMetric 小时级别流量打点追踪'}</p>
      </div>
      
      <div class="h-44 bg-muted/5 rounded-lg flex items-end justify-between gap-1 p-3 mt-auto border border-dashed relative">
        {#each traffic_trends as item}
          <div class="flex flex-col items-center flex-1 h-full justify-end group relative">
            <span class="absolute -top-7 bg-popover text-popover-foreground text-[10px] rounded px-1.5 py-0.5 border opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-sm z-10 pointer-events-none">
              PV: {item.pv} | UV: {item.uv}
            </span>
            <div
              class="bg-gradient-to-t from-sky-500/80 to-sky-600 w-full rounded-t-sm transition-all duration-300 group-hover:brightness-110 min-h-[2px]"
              style="height: {(item.pv / maxTrafficPv) * 100}%"
            ></div>
            {#if parseInt(item.hour) % 4 === 0}
              <span class="text-[9px] text-muted-foreground mt-1.5 scale-90 tracking-tighter">
                {item.hour}
              </span>
            {:else}
              <span class="size-1 bg-muted-foreground/20 rounded-full mt-2"></span>
            {/if}
          </div>
        {/each}
      </div>
    </div>

    <!-- 蜘蛛抓取波动图 -->
    <div class="rounded-xl border bg-card p-5 shadow-sm flex flex-col">
      <div class="mb-4">
        <h2 class="text-sm font-semibold tracking-tight">{t.trend_title || '搜索引擎蜘蛛频次'}</h2>
        <p class="text-xs text-muted-foreground mt-0.5">
          {t.trend_subtitle?.replace('%{engine}', t.search_engine_name || '主流引擎') || '近 7 天搜索引擎爬行趋势波动'}
        </p>
      </div>
      
      <div class="h-44 bg-muted/5 rounded-lg flex items-end justify-around gap-2 p-3 mt-auto border border-dashed relative">
        {#each seo_trend.spider_visits as value, i}
          <div class="flex flex-col items-center flex-1 h-full justify-end group relative">
            <span class="absolute -top-7 bg-popover text-popover-foreground text-[10px] rounded px-1.5 py-0.5 border opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-sm z-10 pointer-events-none">
              {t.trend_tooltip?.replace('%{count}', value.toString()) || `${value} 次抓取`}
            </span>
            <div
              class="bg-gradient-to-t from-amber-500/70 to-amber-500 w-full rounded-t-sm transition-all duration-500 group-hover:brightness-110 min-h-[2px]"
              style="height: {(value / maxSpiderVisits) * 100}%"
            ></div>
            <span class="text-[10px] text-muted-foreground mt-2 tracking-tighter truncate w-full text-center">
              {seo_trend.labels[i]}
            </span>
          </div>
        {/each}
      </div>
    </div>
  </div>

  <!-- ==================== 🛠️ 资产分析排行榜与事件流 ==================== -->
  <div class="grid gap-6 md:grid-cols-5">
    
    <!-- 动态事件流面板 -->
    <div class="rounded-xl border bg-card shadow-sm md:col-span-2 flex flex-col">
      <div class="p-4 border-b flex items-center justify-between">
        <h2 class="text-sm font-semibold tracking-tight">{t.activity_title || '全站动态与捕获'}</h2>
        <span class="text-xs text-muted-foreground">{t.activity_subtitle || '网关事件实时流'}</span>
      </div>
      <div class="divide-y divide-border overflow-y-auto max-h-[360px] flex-1">
        {#if recent_activities.length === 0}
          <div class="flex h-32 items-center justify-center text-xs text-muted-foreground">
            {t.activity_empty || '网关平静如水，暂无捕获动态'}
          </div>
        {:else}
          {#each recent_activities as item (item.id)}
            <div class="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
              <div class="flex items-center gap-3 min-w-0">
                <div class="size-2 rounded-full shrink-0 
                  {item.type === 'seo' ? 'bg-amber-400' : ''}
                  {item.type === 'inquiry' ? 'bg-violet-500 animate-pulse' : ''}
                  {item.type === 'system' ? 'bg-slate-400' : ''}
                  {item.type === 'content' ? 'bg-emerald-400' : ''}
                "></div>
                <div class="min-w-0">
                  <p class="text-sm font-medium truncate text-foreground">{item.action}</p>
                  <p class="text-xs text-muted-foreground truncate mt-0.5">{item.target}</p>
                </div>
              </div>
              <span class="text-xs text-muted-foreground whitespace-nowrap ml-4">{item.time}</span>
            </div>
          {/each}
        {/if}
      </div>
    </div>

    <!-- 资产特征画像多维度 TOP 5 面板 -->
    <div class="rounded-xl border bg-card shadow-sm md:col-span-3 p-4 flex flex-col">
      <div class="mb-4">
        <h2 class="text-sm font-semibold tracking-tight">{t.rankings_title || '今日资产画像排行榜 (TOP 5)'}</h2>
        <p class="text-xs text-muted-foreground mt-0.5">{t.rankings_subtitle || '智能归纳最受青睐的页面、环境与区域来源'}</p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
        <!-- 核心热门受访页面 -->
        <div class="space-y-2">
          <h4 class="text-xs font-semibold flex items-center gap-1 text-muted-foreground border-b pb-1">
            <Layers class="size-3 text-emerald-500" /> {t.rank_pages || '最热访页'}
          </h4>
          <div class="space-y-1.5">
            {#each rankings.pages as item}
              <div class="text-xs">
                <div class="flex justify-between mb-0.5 font-mono text-muted-foreground scale-95 origin-left">
                  <span class="truncate max-w-[140px] text-foreground font-sans">{item.name}</span>
                  <span>{item.value} {t.unit_times || '次'}</span>
                </div>
                <div class="w-full bg-muted rounded-full h-1 overflow-hidden">
                  <div class="bg-emerald-500 h-1 rounded-full" style="width: {Math.min((item.value / (rankings.pages[0]?.value || 1)) * 100, 100)}%"></div>
                </div>
              </div>
            {:else}
              <p class="text-[11px] text-muted-foreground pt-4 text-center">{t.empty_pages || '暂无页面受访数据'}</p>
            {/each}
          </div>
        </div>

        <!-- 地理访问地区排行 -->
        <div class="space-y-2">
          <h4 class="text-xs font-semibold flex items-center gap-1 text-muted-foreground border-b pb-1">
            <Globe class="size-3 text-sky-500" /> {t.rank_regions || '精准客源地'}
          </h4>
          <div class="space-y-1.5">
            {#each rankings.regions as item}
              <div class="text-xs">
                <div class="flex justify-between mb-0.5 font-mono text-muted-foreground scale-95 origin-left">
                  <span class="truncate max-w-[140px] text-foreground font-sans">{item.name}</span>
                  <span>{item.value} {t.unit_times || '次'}</span>
                </div>
                <div class="w-full bg-muted rounded-full h-1 overflow-hidden">
                  <div class="bg-sky-500 h-1 rounded-full" style="width: {Math.min((item.value / (rankings.regions[0]?.value || 1)) * 100, 100)}%"></div>
                </div>
              </div>
            {:else}
              <p class="text-[11px] text-muted-foreground pt-4 text-center">{t.empty_regions || '暂无地区定位记录'}</p>
            {/each}
          </div>
        </div>

        <!-- 操作系统分布 -->
        <div class="space-y-2">
          <h4 class="text-xs font-semibold flex items-center gap-1 text-muted-foreground border-b pb-1">
            <Laptop class="size-3 text-violet-500" /> {t.rank_os || '访客平台 (OS)'}
          </h4>
          <div class="space-y-1.5">
            {#each rankings.os as item}
              <div class="text-xs">
                <div class="flex justify-between mb-0.5 text-muted-foreground scale-95 origin-left">
                  <span class="truncate text-foreground">{item.name}</span>
                  <span>{item.value}</span>
                </div>
                <div class="w-full bg-muted rounded-full h-1 overflow-hidden">
                  <div class="bg-violet-500 h-1 rounded-full" style="width: {Math.min((item.value / (rankings.os[0]?.value || 1)) * 100, 100)}%"></div>
                </div>
              </div>
            {:else}
              <p class="text-[11px] text-muted-foreground pt-4 text-center">{t.empty_os || '等待终端解析...'}</p>
            {/each}
          </div>
        </div>

        <!-- 浏览器分布 -->
        <div class="space-y-2">
          <h4 class="text-xs font-semibold flex items-center gap-1 text-muted-foreground border-b pb-1">
            <Compass class="size-3 text-amber-500" /> {t.rank_browsers || '浏览器内核'}
          </h4>
          <div class="space-y-1.5">
            {#each rankings.browsers as item}
              <div class="text-xs">
                <div class="flex justify-between mb-0.5 text-muted-foreground scale-95 origin-left">
                  <span class="truncate text-foreground">{item.name}</span>
                  <span>{item.value}</span>
                </div>
                <div class="w-full bg-muted rounded-full h-1 overflow-hidden">
                  <div class="bg-amber-500 h-1 rounded-full" style="width: {Math.min((item.value / (rankings.browsers[0]?.value || 1)) * 100, 100)}%"></div>
                </div>
              </div>
            {:else}
              <p class="text-[11px] text-muted-foreground pt-4 text-center">{t.empty_browsers || '等待内核解析...'}</p>
            {/each}
          </div>
        </div>

      </div>
    </div>

  </div>
</div>